import type { AnalysisKey } from '../../commonConstants';
import type {
	PositioningAnalysisSource,
	PositioningLayout,
	PositioningSourceContext,
} from '../../commonTypes';

type PositioningGraphSlice = {
	positioningInsight?: string;
	analyses?: AnalysisKey[];
	positioningLayout?: PositioningLayout;
};

type PositioningDocSlice = {
	ideate?: { data?: PositioningGraphSlice };
	userOne?: {
		ideate?: { data?: PositioningGraphSlice };
		ideateTeam?: { data?: PositioningGraphSlice };
	};
};

const firstFilledAnalyses = (
	...candidates: Array<AnalysisKey[] | undefined>
): AnalysisKey[] | undefined => {
	for (const analyses of candidates) {
		if (analyses && analyses.length) return analyses;
	}
	return undefined;
};

/** Owner-chosen Ideate positioning lives on shared board data, then the owner's graphs. */
export const resolveIdeatePositioning = (
	doc: PositioningDocSlice | null | undefined
): { positioningInsight: string; analyses: AnalysisKey[] } => {
	const shared = doc?.ideate?.data;
	const ownerTeam = doc?.userOne?.ideateTeam?.data;
	const ownerPersonal = doc?.userOne?.ideate?.data;
	return {
		positioningInsight:
			(shared?.positioningInsight || '').trim() ||
			(ownerTeam?.positioningInsight || '').trim() ||
			(ownerPersonal?.positioningInsight || '').trim(),
		analyses:
			firstFilledAnalyses(shared?.analyses, ownerTeam?.analyses, ownerPersonal?.analyses) || [],
	};
};

const firstFilledLayout = (
	...candidates: Array<PositioningLayout | undefined>
): PositioningLayout | undefined => {
	for (const layout of candidates) {
		if (layout && Object.keys(layout).length) return layout;
	}
	return undefined;
};

/** Team Ideate maps share board-level coordinates; personal maps keep their own. */
export const resolveIdeatePositioningLayout = (
	doc: PositioningDocSlice | null | undefined,
	isCollaborative: boolean,
	personalLayout?: PositioningLayout
): PositioningLayout | undefined => {
	if (!isCollaborative) return personalLayout;
	return firstFilledLayout(doc?.ideate?.data?.positioningLayout, personalLayout);
};

export const DEFAULT_POSITIONING_ANALYSES: AnalysisKey[] = [
	'Themes.cluster',
	'Psych.cluster',
	'Motivations.cluster',
	'Archetype.cluster',
];

export const analysesForPositioningGenerate = (analyses: AnalysisKey[] | undefined): AnalysisKey[] => {
	const filtered = (analyses && analyses.length ? analyses : DEFAULT_POSITIONING_ANALYSES).filter(
		(analysis) => analysis !== 'Grounding.cluster'
	);
	return filtered.length ? filtered : DEFAULT_POSITIONING_ANALYSES;
};

/** Ensure a selected child node always carries its cluster centre into generation context. */
export const ensurePositioningClusterCenter = (
	source: PositioningAnalysisSource
): PositioningAnalysisSource => {
	const candidates = [source.cluster, ...source.nodes];
	const seen = new Set<string>();
	const nodes = candidates.filter((label) => {
		const key = label.trim().toLocaleLowerCase();
		if (!key || seen.has(key)) return false;
		seen.add(key);
		return true;
	});
	return { ...source, nodes };
};

export const hasPositioningSourceContext = (
	sourceContext: Pick<
		PositioningSourceContext,
		'analysisTerms' | 'projectFraming' | 'pdfOverview' | 'pdfTopics'
	>
): boolean =>
	sourceContext.analysisTerms.some((term) => term.trim()) ||
	Boolean(sourceContext.projectFraming.trim()) ||
	Boolean(sourceContext.pdfOverview?.trim()) ||
	sourceContext.pdfTopics.length > 0;

export const missingIdeateSourceMessage = (isTeam: boolean): string =>
	isTeam
		? 'Add some ideas on Inspire first before generating on the team Ideate board.'
		: 'Add some of your own ideas on Inspire first before generating on your personal Ideate board.';

export const ideateContextFoundationMessage = (isTeam: boolean): string =>
	isTeam
		? 'Team Ideate uses all ideas on the Inspire board as the foundation for generated outputs.'
		: 'Personal Ideate uses only your own Inspire ideas as the foundation for generated outputs.';

export const ideateContextModeLabel = (isTeam: boolean, nodeCount: number): string =>
	isTeam ? `All Inspire ideas · ${nodeCount}` : `Your Inspire ideas · ${nodeCount}`;

type IdeateContextFile = {
	fileName?: string;
	fileSummary?: string;
	topics?: Array<{ topic?: string }>;
};

export const resolveIdeateContextCopy = ({
	vision,
	summary,
	input,
	boardPdfSummary,
	files,
}: {
	vision?: string;
	summary?: string;
	input?: string;
	boardPdfSummary?: string;
	files?: IdeateContextFile[];
}): { vision: string; pdfSummary: string; pdfTopics: string[] } => {
	const projectVision =
		[vision, summary, input]
			.map((value) => (value || '').trim())
			.find(Boolean) || '';
	const contextFiles = files || [];
	const fileSummaries = contextFiles
		.map((file) => (file.fileSummary || '').trim())
		.filter(Boolean);
	const pdfSummary =
		fileSummaries.join('\n\n') ||
		(boardPdfSummary || '').trim() ||
		contextFiles
			.map((file) => (file.fileName || '').trim())
			.filter(Boolean)
			.join(', ');
	const seenTopics = new Set<string>();
	const pdfTopics: string[] = [];
	for (const file of contextFiles) {
		for (const entry of file.topics || []) {
			const topic = (entry.topic || '').trim();
			const key = topic.toLocaleLowerCase();
			if (!topic || seenTopics.has(key)) continue;
			seenTopics.add(key);
			pdfTopics.push(topic);
		}
	}
	return { vision: projectVision, pdfSummary, pdfTopics };
};
